import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheckCircle, 
    faTimesCircle, 
    faSpinner,
    faServer,
    faEnvelope,
    faCloud,
    faSync,
    faClock,
    faTasks,
    faExclamationTriangle,
    faCheck,
    faHistory,
    faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const AdminItDashboard = () => {
    const [healthData, setHealthData] = useState({
        limesurvey: { status: 'loading', connected: false, message: 'Checking...' },
        resend: { status: 'loading', connected: false, message: 'Checking...' },
        sftp: { status: 'loading', connected: false, message: 'Checking...' },
        scheduler: { status: 'loading', running: false, tasks: [] }
    });
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);

    useEffect(() => {
        fetchHealthStatus();
        // Refresh every 5 minutes
        const interval = setInterval(fetchHealthStatus, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const fetchHealthStatus = async () => {
        try {
            setLoading(true);
            const response = await api.get('/system/health');
            if (response.data.success) {
                setHealthData(response.data.data);
                setLastUpdate(new Date());
            }
        } catch (error) {
            console.error('Error fetching system health:', error);
            setHealthData({
                limesurvey: { status: 'error', connected: false, message: 'Failed to check status' },
                resend: { status: 'error', connected: false, message: 'Failed to check status' },
                sftp: { status: 'error', connected: false, message: 'Failed to check status' },
                scheduler: { status: 'error', running: false, tasks: [] }
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'operational':
                return <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-2xl" />;
            case 'error':
                return <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 text-2xl" />;
            default:
                return <FontAwesomeIcon icon={faSpinner} className="text-gray-400 text-2xl animate-spin" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'operational':
                return 'bg-green-50 border-green-200';
            case 'error':
                return 'bg-red-50 border-red-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'operational':
                return 'Opérationnel';
            case 'error':
                return 'Erreur';
            default:
                return 'Vérification...';
        }
    };

    const formatLastCheck = (timestamp) => {
        if (!timestamp) return 'Jamais';
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diff = Math.floor((now - date) / 1000);
            
            if (diff < 60) return `Il y a ${diff} secondes`;
            if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} minutes`;
            if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} heures`;
            return date.toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return 'Inconnu';
        }
    };

    const formatNextExecution = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diff = Math.floor((date - now) / 1000);
            
            // If it's today, show time only
            if (date.toDateString() === now.toDateString()) {
                return `Aujourd'hui à ${date.toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                })}`;
            }
            
            // If it's tomorrow
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (date.toDateString() === tomorrow.toDateString()) {
                return `Demain à ${date.toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                })}`;
            }
            
            // Otherwise show full date and time
            return date.toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return 'Inconnu';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
                                Tableau de Bord
                            </h1>
                            <p className="text-gray-600">Surveillance et statut des systèmes</p>
                        </div>
                        <button
                            onClick={fetchHealthStatus}
                            disabled={loading}
                            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FontAwesomeIcon icon={faSync} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Actualiser
                        </button>
                    </div>
                    {lastUpdate && (
                        <p className="text-sm text-gray-500">
                            Dernière mise à jour: {formatLastCheck(lastUpdate.toISOString())}
                        </p>
                    )}
                </div>

                {/* Bento Grid - System Health Section */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                    {/* Section Title */}
                    <div className="lg:col-span-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Statut des Intégrations</h2>
                    </div>

                    {/* LimeSurvey Card */}
                    <div className={`lg:col-span-4 bg-white rounded-3xl p-6 shadow-xl border-2 ${getStatusColor(healthData.limesurvey.status)} transition-all hover:shadow-2xl`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                                    <FontAwesomeIcon icon={faServer} className="text-red-600 text-xl" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">LimeSurvey API</h3>
                                    <p className="text-xs text-gray-500">Service d'enquêtes</p>
                                </div>
                            </div>
                            {getStatusIcon(healthData.limesurvey.status)}
                        </div>
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Statut:</span>
                                <span className={`text-sm font-bold ${
                                    healthData.limesurvey.status === 'operational' ? 'text-green-600' : 
                                    healthData.limesurvey.status === 'error' ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                    {getStatusText(healthData.limesurvey.status)}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">{healthData.limesurvey.message}</p>
                            {healthData.limesurvey.last_check && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Vérifié: {formatLastCheck(healthData.limesurvey.last_check)}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Resend Email Card */}
                    <div className={`lg:col-span-4 bg-white rounded-3xl p-6 shadow-xl border-2 ${getStatusColor(healthData.resend.status)} transition-all hover:shadow-2xl`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                                    <FontAwesomeIcon icon={faEnvelope} className="text-red-600 text-xl" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Resend Email</h3>
                                    <p className="text-xs text-gray-500">Service d'email</p>
                                </div>
                            </div>
                            {getStatusIcon(healthData.resend.status)}
                        </div>
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Statut:</span>
                                <span className={`text-sm font-bold ${
                                    healthData.resend.status === 'operational' ? 'text-green-600' : 
                                    healthData.resend.status === 'error' ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                    {getStatusText(healthData.resend.status)}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">{healthData.resend.message}</p>
                            {healthData.resend.last_check && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Vérifié: {formatLastCheck(healthData.resend.last_check)}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* SFTP Card */}
                    <div className={`lg:col-span-4 bg-white rounded-3xl p-6 shadow-xl border-2 ${getStatusColor(healthData.sftp.status)} transition-all hover:shadow-2xl`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                                    <FontAwesomeIcon icon={faCloud} className="text-red-600 text-xl" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">SFTP</h3>
                                    <p className="text-xs text-gray-500">Stockage de fichiers</p>
                                </div>
                            </div>
                            {getStatusIcon(healthData.sftp.status)}
                        </div>
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Statut:</span>
                                <span className={`text-sm font-bold ${
                                    healthData.sftp.status === 'operational' ? 'text-green-600' : 
                                    healthData.sftp.status === 'error' ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                    {getStatusText(healthData.sftp.status)}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">{healthData.sftp.message}</p>
                            {healthData.sftp.host && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Host: {healthData.sftp.host}:{healthData.sftp.port || 22}
                                </p>
                            )}
                            {healthData.sftp.last_check && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Vérifié: {formatLastCheck(healthData.sftp.last_check)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Scheduler Status Section */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                    <div className="lg:col-span-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Scheduler</h2>
                    </div>

                    {/* Scheduler Main Card */}
                    <div className={`lg:col-span-12 bg-white rounded-3xl p-6 shadow-xl border-2 ${
                        healthData.scheduler?.status === 'operational' ? 'border-green-200' : 
                        healthData.scheduler?.status === 'warning' ? 'border-yellow-200' : 
                        healthData.scheduler?.status === 'error' ? 'border-red-200' :
                        'border-gray-200'
                    } transition-all hover:shadow-2xl`}>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                                    <FontAwesomeIcon icon={faClock} className="text-red-600 text-2xl" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Scheduler</h3>
                                    <p className="text-sm text-gray-500">Tâches programmées et exécutions</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                {healthData.scheduler?.status === 'operational' && (
                                    <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold text-sm">
                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                        Actif
                                    </span>
                                )}
                                {healthData.scheduler?.status === 'warning' && (
                                    <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg font-semibold text-sm">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                        Attention
                                    </span>
                                )}
                                {healthData.scheduler?.status === 'error' && (
                                    <span className="px-4 py-2 bg-red-100 text-red-800 rounded-lg font-semibold text-sm">
                                        <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                                        Erreur
                                    </span>
                                )}
                                {healthData.scheduler?.status === 'loading' && (
                                    <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-semibold text-sm">
                                        <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                        Vérification...
                                    </span>
                                )}
                            </div>
                        </div>

                        {healthData.scheduler?.tasks && healthData.scheduler.tasks.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {healthData.scheduler.tasks.map((task, index) => (
                                    <div 
                                        key={index}
                                        className={`p-4 rounded-xl border-2 ${
                                            task.status === 'active' ? 'bg-green-50 border-green-200' :
                                            task.status === 'inactive' ? 'bg-yellow-50 border-yellow-200' :
                                            'bg-gray-50 border-gray-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <h4 className="font-bold text-gray-900 text-sm">{task.name}</h4>
                                            {task.status === 'active' && (
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-sm" />
                                            )}
                                            {task.status === 'inactive' && (
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-sm" />
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-600 mb-2">
                                            {task.schedule_time && !task.schedule.includes('at') 
                                                ? `${task.schedule} at ${task.schedule_time}` 
                                                : task.schedule}
                                        </p>
                                        {task.last_execution && (
                                            <p className="text-xs text-gray-500">
                                                Dernière exécution: {formatLastCheck(task.last_execution)}
                                            </p>
                                        )}
                                        {!task.last_execution && (
                                            <p className="text-xs text-gray-500">Aucune exécution enregistrée</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : healthData.scheduler?.status !== 'loading' ? (
                            <div className="text-center py-8 text-gray-500">
                                <p>Aucune tâche programmée trouvée</p>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <FontAwesomeIcon icon={faSpinner} className="text-3xl mb-2 animate-spin" />
                                <p>Chargement des tâches...</p>
                            </div>
                        )}

                        {healthData.scheduler?.active_tasks !== undefined && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">
                                        Tâches actives: <span className="font-bold text-gray-900">{healthData.scheduler.active_tasks}</span> / {healthData.scheduler.total_tasks}
                                    </span>
                                    {healthData.scheduler.last_check && (
                                        <span className="text-gray-500">
                                            Vérifié: {formatLastCheck(healthData.scheduler.last_check)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminItDashboard;
